
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SavedDestinations } from './SavedDestinations';
import { FavoriteHosts } from './FavoriteHosts';
import { UpcomingSessions } from './UpcomingSessions';
import { PastSessions } from './PastSessions';
import { useEffect, useState } from 'react';
import { CalendarCheck } from 'lucide-react';

interface TripsTabsProps {
  defaultTab: string;
  favorites: any[];
  sessions: any[];
  onCancelSession: (sessionId: string) => void;
  onJoinMeeting: (meetingLink: string | null, e: React.MouseEvent) => void;
}

export const TripsTabs = ({
  defaultTab,
  favorites,
  sessions,
  onCancelSession,
  onJoinMeeting
}: TripsTabsProps) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  // Update active tab when defaultTab prop changes
  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const destinationFavorites = favorites.filter(f => f.type === 'destination') || [];
  const hostFavorites = favorites.filter(f => f.type === 'host') || [];
  
  const upcomingSessions = sessions?.filter(
    (s) => new Date(s.session.start_time) > new Date()
  ).sort((a, b) => 
    new Date(a.session.start_time).getTime() - new Date(b.session.start_time).getTime()
  ) || [];
  
  const pastSessions = sessions?.filter(
    (s) => new Date(s.session.start_time) <= new Date()
  ).sort((a, b) => 
    new Date(b.session.start_time).getTime() - new Date(a.session.start_time).getTime()
  ) || [];

  return (
    <Tabs 
      value={activeTab} 
      onValueChange={setActiveTab}
      className="space-y-6"
    >
      <TabsList className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 w-full">
        <TabsTrigger value="destinations">Saved Places</TabsTrigger>
        <TabsTrigger value="hosts">Favorite Hosts</TabsTrigger>
        <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
        <TabsTrigger value="past">Past Sessions</TabsTrigger>
        <TabsTrigger value="bookings" className="flex items-center justify-center gap-1">
          <CalendarCheck className="h-3.5 w-3.5 hidden sm:inline" />
          <span>My Bookings</span>
        </TabsTrigger>
      </TabsList>

      <TabsContent value="destinations">
        <SavedDestinations favorites={destinationFavorites} />
      </TabsContent>

      <TabsContent value="hosts">
        <FavoriteHosts favorites={hostFavorites} />
      </TabsContent>

      <TabsContent value="upcoming">
        <UpcomingSessions 
          sessions={upcomingSessions} 
          onCancelSession={onCancelSession}
          onJoinMeeting={onJoinMeeting}
        />
      </TabsContent>

      <TabsContent value="past">
        <PastSessions sessions={pastSessions} />
      </TabsContent>

      <TabsContent value="bookings">
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-lg font-medium mb-2">Your Bookings</h3>
          <p className="text-gray-500">This is where your bookings will appear.</p>
        </div>
      </TabsContent>
    </Tabs>
  );
};
